import { Popconfirm } from '@/components/Popconfirm';
import { Text } from '@/components/ui';
import { Checkbox } from '@/components/ui/Checkbox';
import { Table } from '@/components/ui/Table';
import { ColumnDef } from '@tanstack/react-table';
import { format } from 'date-fns';
import { TrashIcon } from 'lucide-react';
import { useMemo } from 'react';
import type { TicketAppointment } from './TicketForm';
import { useUsers } from '@/hooks/useUsers';
import { useTranslation } from 'react-i18next';

interface TicketFormAppointmentsTableProps {
  appointments: TicketAppointment[];
  onRemoveAppointment: (index: number) => void;
}

export function TicketFormAppointmentsTable({
  appointments,
  onRemoveAppointment,
}: TicketFormAppointmentsTableProps) {
  const { users } = useUsers();
  const { t } = useTranslation();

  const columns: ColumnDef<TicketAppointment & { index: number }>[] = useMemo(
    () => [
      {
        id: 'date',
        header: t('tickets.appointmentTable.date'),
        cell: ({ row: { original } }) => (
          <Text variant="label" size="sm">
            {format(original.date, 'dd/MM/yyyy')}
          </Text>
        ),
      },
      {
        id: 'user',
        header: t('tickets.appointmentTable.responsible'),
        cell: ({ row: { original } }) => {
          const user = users.find(({ id }) => original.userId === id);
          return (
            <Text variant="label" size="sm">
              {user?.name}
            </Text>
          );
        },
      },
      {
        id: 'workedTime',
        header: t('tickets.appointmentTable.actionTime'),
        cell: ({ row: { original } }) => (
          <Text
            variant="label"
            size="sm"
          >{`${original.workedTime} ${original.timeUnit === 'hours' ? 'h' : 'm'}`}</Text>
        ),
      },
      {
        accessorKey: 'isDoubleDebited',
        header: t('tickets.appointmentTable.extra'),
        cell: ({ row: { original } }) => (
          <Checkbox checked={original.isDoubleDebited} className="scale-[85%]" disabled />
        ),
      },
      {
        id: 'actions',
        cell: ({ row: { original } }) => (
          <div className="flex items-center">
            <Popconfirm
              title={t('tickets.appointmentTable.deleteTitle')}
              description={t('tickets.appointmentTable.deleteDescription')}
              confirmLabel={t('common.delete')}
              onConfirm={() => onRemoveAppointment(original.index)}
              variant="danger"
            >
              <button
                type="button"
                className="text-neutral-muted hover:text-error-default outline-neutral-subtle cursor-pointer rounded-full transition-all focus:outline-2 focus:outline-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <TrashIcon size={16} />
              </button>
            </Popconfirm>
          </div>
        ),
      },
    ],
    [onRemoveAppointment, t, users]
  );

  const appointmentsWithIndex = appointments.map((appointment, index) => ({
    ...appointment,
    index,
  }));

  return <Table columns={columns} data={appointmentsWithIndex} />;
}
